/**
 * Report Tabs Component
 *
 * Component for displaying different report types in tabs following clean architecture patterns
 */

import React, {useState, useCallback} from 'react';
import {Marketer} from '../models/report.model';
import {StoresReportTab} from './StoresReportTab';
import {FinancialReportTab} from './FinancialReportTab';
import {ExportTab} from './ExportTab';
import {AppCardTitle, AppText, AppButtonText, AppSmallText} from '../../../Presentation/components/ui/Typography';
import Card from '../../../Presentation/components/ui/Card';

interface ReportTabsProps {
  marketer: Marketer;
}

type TabType = 'stores' | 'financial' | 'export';

export const ReportTabs: React.FC<ReportTabsProps> = ({marketer}) => {
  const [activeTab, setActiveTab] = useState<TabType>('stores');

  // Tab configuration
  const tabs = [
    {
      id: 'stores' as const,
      label: 'تقرير المتاجر',
      icon: '🏪',
      description: 'عرض تفاصيل المتاجر والمبيعات'
    },
    {
      id: 'financial' as const,
      label: 'التقرير المالي',
      icon: '💰',
      description: 'عرض البيانات المالية والإحصائيات'
    },
    {
      id: 'export' as const,
      label: 'تصدير التقرير',
      icon: '📊',
      description: 'تصدير التقرير بصيغ مختلفة'
    }
  ] as const;

  // Handle tab change
  const handleTabChange = useCallback((tabId: TabType) => {
    setActiveTab(tabId);
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <AppCardTitle className="text-gray-900 mb-2">
          تقارير المسوق: {marketer.name}
        </AppCardTitle>
        <AppText className="text-gray-600">
          اختر نوع التقرير المطلوب لعرض البيانات التفصيلية
        </AppText>
      </div>

      {/* Tab Navigation */}
      <Card className="p-1">
        <nav className="flex space-x-1 space-x-reverse">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex-1 flex flex-col items-center py-4 px-3 rounded-lg transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-brand-50 text-brand-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="text-2xl mb-2">{tab.icon}</div>
              <AppButtonText className={activeTab === tab.id ? 'text-brand-700' : 'text-gray-700'}>
                {tab.label}
              </AppButtonText>
              <AppSmallText className={`mt-1 ${activeTab === tab.id ? 'text-brand-600' : 'text-gray-500'}`}>
                {tab.description}
              </AppSmallText>
            </button>
          ))}
        </nav>
      </Card>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'stores' && <StoresReportTab marketer={marketer} />}
        {activeTab === 'financial' && <FinancialReportTab marketer={marketer} />}
        {activeTab === 'export' && <ExportTab marketer={marketer} />}
      </div>
    </div>
  );
};
